/**
 * Taking a live AI call over (ring R6): the one request the dock makes, and
 * what its refusals mean.
 *
 * Every refusal has its own word because they mean different things to the
 * person pressing: "your mobile is the number this call is ringing" is a thing
 * they can fix in seconds, "a colleague got there first" is not a fault at all,
 * and "the call is over" means stop looking at the card. A single "failed"
 * would make all three look like a broken button.
 *
 * React-free, so the readers run under plain node.
 */
import { ApiError, api } from "./api";
import type { TakeoverVia } from "./aiLive";

/** What `POST .../takeover` answers with when it worked. */
export interface TakeoverStarted {
  via: TakeoverVia;
  /** This operator's own attempt was already running: the press changed nothing. */
  already: boolean;
}

export async function startAiTakeover(callLogId: string, via: TakeoverVia): Promise<TakeoverStarted> {
  const raw = await api.post<{ via?: unknown; already?: unknown } | null>(
    `/admin/crm/calls/ai-outbound/${encodeURIComponent(callLogId)}/takeover`,
    { via },
  );
  return { via: raw?.via === "browser" ? "browser" : "mobile", already: raw?.already === true };
}

/**
 * Why this browser calls its own attempt off (ring R6d). Mirrors
 * `TAKEOVER_CANCEL_REASONS` in the server's `takeover/cancel.ts`.
 */
export const TAKEOVER_CANCEL_REASONS = ["browser_failed", "operator_left"] as const;
export type TakeoverCancelReason = (typeof TAKEOVER_CANCEL_REASONS)[number];

/**
 * Call this operator's own attempt off: the browser leg is not coming.
 *
 * Safe to send whenever a takeover call's leg ends. After the bridge the server
 * answers `cancelled: false` and changes nothing, so the browser never has to
 * know for certain whether the two people were already talking.
 */
export async function cancelAiTakeover(callLogId: string, reason: TakeoverCancelReason): Promise<boolean> {
  const raw = await api.post<{ cancelled?: unknown } | null>(
    `/admin/crm/calls/ai-outbound/${encodeURIComponent(callLogId)}/takeover/cancel`,
    { reason },
  );
  return raw?.cancelled === true;
}

/**
 * The words the dock has a sentence for. Mirrors the route's own answers, plus
 * `forbidden` and `failed` for anything that is not one of them.
 */
export const TAKEOVER_ERRORS = [
  "not_found",
  "not_yours",
  "not_talking",
  "opening",
  "machine",
  "stopped",
  "record_objected",
  "taken",
  "gone",
  "no_mobile",
  "bad_mobile",
  "mobile_suppressed",
  "same_number",
  "leg_failed",
  "forbidden",
  "failed",
] as const;
export type TakeoverError = (typeof TAKEOVER_ERRORS)[number];

export function takeoverError(err: unknown): TakeoverError {
  if (!(err instanceof ApiError)) return "failed";
  const code = (err.body as { error?: unknown } | null)?.error;
  if ((TAKEOVER_ERRORS as readonly unknown[]).includes(code)) return code as TakeoverError;
  return err.status === 403 ? "forbidden" : "failed";
}

/**
 * Stopping the recording of a taken-over call's person half (ring R6c): the
 * words the dock's stop has a sentence for.
 *
 * The request itself is the call window's own `stopCallRecording`, on purpose:
 * one route and one rule (`record-stop.ts`) for every "stop recording" button,
 * so the dock cannot offer a stop the route would refuse for a reason the call
 * window never heard of. `already_stopped` is not here because the route
 * answers it with a 200.
 */
export const RECORD_STOP_ERRORS = [
  "not_live",
  "takeover_not_bridged",
  "not_recording",
  "no_leg",
  "twilio_failed",
  "not_found",
  "forbidden",
  "failed",
] as const;
export type RecordStopError = (typeof RECORD_STOP_ERRORS)[number];

export function recordStopError(err: unknown): RecordStopError {
  if (!(err instanceof ApiError)) return "failed";
  const code = (err.body as { error?: unknown } | null)?.error;
  if ((RECORD_STOP_ERRORS as readonly unknown[]).includes(code)) return code as RecordStopError;
  return err.status === 403 ? "forbidden" : "failed";
}
